// StateMachineAdapter.ts
// Adapter that bridges XState survey machine with existing UI API
// Provides the same interface as SurveyEngineV2 but uses state machine underneath

import { createActor } from 'xstate';
import { surveyMachine } from '../machines/surveyMachine.ts';
import { getQuestionData } from '../data/questionData.ts';
import type { QuestionData } from '../machines/surveyMachine.types.ts';

/**
 * Adapter that makes the XState machine compatible with existing UI
 * Implements the same API as SurveyEngineV2
 */
export class StateMachineAdapter {
  private actor: ReturnType<typeof createActor<typeof surveyMachine>>;
  private storageKey = 'surveyProgressStateMachine';
  private stateHistory: string[] = []; // Track state history for back navigation

  constructor() {
    // Create the state machine actor
    this.actor = createActor(surveyMachine);
    this.actor.start();

    // Subscribe to state changes to track history
    this.actor.subscribe((state) => {
      // Don't add transitional states to history
      const currentState = state.value as string;
      if (!currentState.includes('Path') || !currentState.includes('Start')) {
        // Track state in history (but don't duplicate if same as last)
        if (this.stateHistory[this.stateHistory.length - 1] !== currentState) {
          this.stateHistory.push(currentState);
        }
      }
    });
  }

  /**
   * Initialize the survey (required by UI)
   */
  async loadSurvey() {
    // Machine starts in Q1 state automatically
    // Nothing special needed here
    return Promise.resolve();
  }

  /**
   * Get current question data
   * Returns question data compatible with UI expectations
   */
  getCurrentQuestion(): QuestionData | null {
    const currentState = this.actor.getSnapshot().value as string;

    // Handle completion state (final state with no question data)
    if (currentState === 'Complete') {
      return null;
    }

    // Handle path start states (transitional states with no question data)
    if (currentState.includes('Path') && currentState.includes('Start')) {
      return null;
    }

    // Get question data for current state
    const questionData = getQuestionData(currentState);

    if (!questionData) {
      console.error(`No question data found for state: ${currentState}`);
      return null;
    }

    return questionData;
  }

  /**
   * Get question history for back navigation
   */
  getQuestionHistory(): QuestionData[] {
    // Use local state history instead of context history
    return this.stateHistory
      .map(stateId => getQuestionData(stateId))
      .filter(Boolean) as QuestionData[];
  }

  /**
   * Set answer for current question
   * This updates the machine context
   */
  setAnswer(questionId: string, value: any) {
    const currentQuestion = this.getCurrentQuestion();

    if (!currentQuestion) {
      console.warn('No current question to set answer for');
      return;
    }

    // Don't send ANSWER event yet, just store it temporarily
    // The actual event will be sent when nextQuestion() is called
    // This matches the existing UI flow
    this._pendingAnswer = { questionId, value };
  }

  private _pendingAnswer: { questionId: string; value: any } | null = null;

  /**
   * Advance to next question
   * Sends ANSWER event to state machine with pending answer
   */
  nextQuestion(): QuestionData | null {
    if (!this._pendingAnswer) {
      console.warn('No pending answer to submit');
      return this.getCurrentQuestion();
    }

    // Send ANSWER event to state machine
    this.actor.send({
      type: 'ANSWER',
      value: this._pendingAnswer.value,
      questionId: this._pendingAnswer.questionId,
    });

    // Clear pending answer
    this._pendingAnswer = null;

    // Handle transitional states (PathA_Start, etc.)
    // The machine will auto-transition using 'always' guards
    let currentState = this.actor.getSnapshot().value as string;
    let iterations = 0;
    const maxIterations = 10; // Prevent infinite loops

    while (
      (currentState.includes('Path') && currentState.includes('Start')) &&
      iterations < maxIterations
    ) {
      // Wait for auto-transition
      // In a real implementation, this would be handled by the machine's 'always' transitions
      currentState = this.actor.getSnapshot().value as string;
      iterations++;
    }

    return this.getCurrentQuestion();
  }

  /**
   * Go back to previous question
   * Sends BACK event to state machine
   */
  previousQuestion(): QuestionData | null {
    this.actor.send({ type: 'BACK' });

    // Handle transitional states
    let currentState = this.actor.getSnapshot().value as string;
    let iterations = 0;
    const maxIterations = 10;

    while (
      (currentState.includes('Path') && currentState.includes('Start')) &&
      iterations < maxIterations
    ) {
      currentState = this.actor.getSnapshot().value as string;
      iterations++;
    }

    return this.getCurrentQuestion();
  }

  /**
   * Get survey progress
   * Reads from machine context
   */
  getProgress() {
    const context = this.actor.getSnapshot().context;
    return context.progress;
  }

  /**
   * Check if survey is complete
   */
  isComplete(): boolean {
    const currentState = this.actor.getSnapshot().value as string;
    return currentState === 'Complete';
  }

  /**
   * Validate current answer
   * Checks if current question has been answered
   */
  validateCurrentAnswer(): boolean {
    const currentQuestion = this.getCurrentQuestion();

    if (!currentQuestion) return true;

    // Check if we have a pending answer
    if (!this._pendingAnswer) return false;

    const value = this._pendingAnswer.value;

    // Check required questions
    if (currentQuestion.required) {
      if (value === undefined || value === null || value === '') {
        return false;
      }

      // Check array values for multi-select
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
    }

    // Check max selections for multi-choice
    if (currentQuestion.type === 'multi-choice' && currentQuestion.maxSelections) {
      if (Array.isArray(value) && value.length > currentQuestion.maxSelections) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get all answers (for submission/export)
   */
  getAnswers(): Record<string, any> {
    const context = this.actor.getSnapshot().context;
    return context.answers;
  }

  /**
   * Expose answers as a property for UI compatibility
   * Includes both saved answers and pending answer (for multi-choice)
   */
  get answers(): Record<string, any> {
    const context = this.actor.getSnapshot().context;
    const allAnswers = { ...context.answers };

    // Include pending answer for UI checks (important for multi-choice)
    if (this._pendingAnswer) {
      allAnswers[this._pendingAnswer.questionId] = this._pendingAnswer.value;
    }

    return allAnswers;
  }

  /**
   * Get current path (A/B/C/D)
   */
  getCurrentPath(): string | null {
    const context = this.actor.getSnapshot().context;
    return context.currentPath;
  }

  /**
   * Save progress to localStorage
   */
  saveProgress() {
    const snapshot = this.actor.getSnapshot();
    const data = {
      state: snapshot.value,
      context: snapshot.context,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }

  /**
   * Load progress from localStorage
   * Returns saved progress data or null
   */
  loadProgress(): any {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) return null;

      const data = JSON.parse(saved);

      // Check if saved data is recent (within 24 hours)
      const age = Date.now() - data.timestamp;
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours

      if (age > maxAge) {
        localStorage.removeItem(this.storageKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to load progress:', error);
      return null;
    }
  }

  /**
   * Restore progress from saved data
   */
  restoreProgress(savedData: any): boolean {
    try {
      if (!savedData) return false;

      // Restore state (would need machine rehydration in full implementation)
      // For now, we'd need to replay events to get to the saved state
      // This is a simplified version

      return true;
    } catch (error) {
      console.error('Failed to restore progress:', error);
      return false;
    }
  }

  /**
   * Clear progress
   */
  clearProgress() {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Get survey metadata (for completion screen)
   */
  getSurveyData() {
    return {
      title: 'The Snake Project Survey',
      description: 'A survey about work-life culture in Portugal',
      theme: { accentColor: '#B8860B' },
      completion: { message: 'Thank you for your participation!' }
    };
  }

  /**
   * Skip current question (if optional)
   */
  skipQuestion(): QuestionData | null {
    this.actor.send({ type: 'SKIP' });
    return this.getCurrentQuestion();
  }

  /**
   * Restart survey from beginning
   */
  restart() {
    this.actor.send({ type: 'RESTART' });
    this.clearProgress();
  }

  /**
   * Get current state name (for debugging)
   */
  getCurrentStateName(): string {
    return this.actor.getSnapshot().value as string;
  }

  /**
   * Subscribe to state changes
   * Allows UI to react to machine state changes
   */
  subscribe(callback: (state: any) => void) {
    return this.actor.subscribe(callback);
  }
}
